import { useEffect, useState, useCallback, useRef } from "react";
import { 
    DollarSign, 
    Users, 
    BookOpen, 
    ShieldAlert, 
    TrendingUp, 
    ChevronLeft, 
    ChevronRight, 
    Inbox, 
    Library, 
    Search, 
    X, 
    CheckCircle2 
} from "lucide-react";
import api from "../../connector/api";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import AppLayout from "../components/common/AppLagout";

function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler); // Fixed typo here
    }, [value, delay]);
    return debouncedValue;
}

export default function FineDashboard() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, 400);

    // Server-Controlled Slicing States
    const [manifestPage, setManifestPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const itemsPerPage = 12;

    // Local client UI paging slice for the aggregated student dictionary list
    const [debtorPage, setDebtorPage] = useState(1);

    const [summary, setSummary] = useState({
        outstanding_fine: 0,
        total_fine_paid: 0,
        total_fine_all: 0,
    });

    const [students, setStudents] = useState([]);
    const [loans, setLoans] = useState([]);

    // --- MODAL STATES ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedLoan, setSelectedLoan] = useState(null);
    const [submittingPayment, setSubmittingPayment] = useState(false);

    // 2. HEADER-LEVEL SEARCH STATE
    const searchRef = useRef(null);
    const [headerSearchTerm, setHeaderSearchTerm] = useState("");
    const debouncedHeaderSearchTerm = useDebounce(headerSearchTerm, 400);
    const [headerSearchLoading, setHeaderSearchLoading] = useState(false);
    const [headerSearchResults, setHeaderSearchResults] = useState([]);
    const [headerShowDropdown, setHeaderShowDropdown] = useState(false);
    const [headerHighlightedIndex, setHeaderHighlightedIndex] = useState(-1);

    useEffect(() => {
        if (!headerSearchTerm.trim()) {
        setHeaderSearchResults([]);
        setHeaderShowDropdown(false);
        return;
        }

        setHeaderSearchLoading(true);
        const handler = setTimeout(async () => {
        try {
            const res = await api.get("/cataloging/dashboard/search", {
            params: { q: headerSearchTerm },
            });
            const data = res.data || [];
            const normalizedResults = data.map((b) => ({
            ...b,
            cover_url: b.cover_image?.startsWith("http")
                ? b.cover_image
                : b.cover_image
                ? `${api.defaults.baseURL.replace("/api", "")}${b.cover_image}`
                : null,
            is_available: b.status === "available",
            }));
            setHeaderSearchResults(normalizedResults);
            setHeaderShowDropdown(true);
        } catch (err) {
            console.error("Header search failed:", err);
            setHeaderSearchResults([]);
        } finally {
            setHeaderSearchLoading(false);
        }
        }, 400);

        return () => clearTimeout(handler);
    }, [headerSearchTerm]);

    // Consolidated dynamic server fetch execution loop for Fine Dashboard
    const fetchFineDashboard = useCallback(async (page, search) => {
        setLoading(true);
        try {
            const res = await api.get(`loans/fines/?page=${page}&page_size=${itemsPerPage}&search=${encodeURIComponent(search)}`);
            
            setSummary({
                outstanding_fine: res.data.outstanding_fine || 0,
                total_fine_paid: res.data.total_fine_paid || 0,
                total_fine_all: res.data.total_fine_all || 0,
            });
            setStudents(res.data.students_with_fines || []);
            setLoans(res.data.loans_with_fines || []);
            setTotalRecords(res.data.total_records || 0);
        } catch (err) {
            toast.error("Failed to load fine dashboard metrics.");
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch header book search results when typing in header
    useEffect(() => {
        const fetchHeaderBooks = async () => {
            if (!debouncedHeaderSearchTerm.trim()) {
                setHeaderSearchResults([]);
                setHeaderShowDropdown(false);
                return;
            }

            setHeaderSearchLoading(true);
            try {
                const res = await api.get(`books/?search=${encodeURIComponent(debouncedHeaderSearchTerm)}`);
                setHeaderSearchResults(res.data.results || res.data || []);
                setHeaderShowDropdown(true);
            } catch (err) {
                setHeaderSearchResults([]);
            } finally {
                setHeaderSearchLoading(false);
            }
        };

        fetchHeaderBooks();
    }, [debouncedHeaderSearchTerm]);

    // Monitor engine triggers
    useEffect(() => {
        fetchFineDashboard(manifestPage, debouncedSearchTerm);
    }, [manifestPage, debouncedSearchTerm, fetchFineDashboard]);

    // Track filtering resets
    useEffect(() => {
        setManifestPage(1);
        setDebtorPage(1);
    }, [debouncedSearchTerm]);

    // Manifest paging configurations
    const totalManifestPages = Math.ceil(totalRecords / itemsPerPage) || 1;

    // Student layout page calculations
    const totalDebtorPages = Math.ceil(students.length / itemsPerPage) || 1;
    const currentStudents = students.slice(
        (debtorPage - 1) * itemsPerPage,
        debtorPage * itemsPerPage
    );

    // --- PAYMENT HANDLER ---
    const handleOpenPaymentModal = (loan) => {
        setSelectedLoan(loan);
        setIsModalOpen(true);
    };

    const handleConfirmPayment = async () => {
        if (!selectedLoan) return;
        setSubmittingPayment(true);
        try {
            await api.post(`loans/${selectedLoan.fine_id}/pay/`);
            toast.success(`Fine for ${selectedLoan.patron_name} settled!`);
            setIsModalOpen(false);
            setSelectedLoan(null);
            
            fetchFineDashboard(manifestPage, debouncedSearchTerm);
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to update payment status.");
        } finally {
            setSubmittingPayment(false);
        }
    };

    const handleSelectHeaderBook = (book) => {
        setHeaderShowDropdown(false);
        setHeaderSearchTerm("");
        setHeaderHighlightedIndex(-1);
        navigate(`/library/cataloging/book/detail/${book.id}`);
    };

    return (
        <AppLayout
            headerProps={{
                title: "Pay Fine",
                searchTerm: headerSearchTerm,
                setSearchTerm: setHeaderSearchTerm,
                searchRef,
                searchLoading: headerSearchLoading,
                searchResults: headerSearchResults,
                showDropdown: headerShowDropdown,
                setShowDropdown: setHeaderShowDropdown,
                highlightedIndex: headerHighlightedIndex,
                setHighlightedIndex: setHeaderHighlightedIndex,
                handleSelectBook: handleSelectHeaderBook,
            }}
        >
            <Toaster position="top-right" />

            <div className="space-y-6 font-dynamic-title p-2 bg-[#120f0d] text-stone-200 min-h-screen">
                
                {/* PAGE TITLE BAR */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-[#2e2720]">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-400">
                            <Library className="w-5 h-5" />
                        </div>
                        <div>
                            <h1 className="text-lg font-bold text-stone-100 tracking-tight">Pay Fine</h1>
                            <p className="text-xs text-stone-400 mt-0.5">Fee audits, accounts receivable, and patron violation balances</p>
                        </div>
                    </div>
                </div>

                {/* FILTER UTILITY CONTROL DECK */}
                <div className="bg-[#1a1613] rounded-2xl border border-[#2e2720] p-4 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-xl">
                    <div className="relative w-full sm:w-96">
                        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
                        <input
                            type="text"
                            placeholder="Search student or book title..."
                            className="w-full bg-[#131110] border border-[#2e2720] text-stone-200 pl-10 pr-8 h-10 rounded-xl text-xs placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500/50 transition font-mono"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm("")}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300"
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                    <div className="text-xs text-stone-400 font-mono">
                        {loading ? "Syncing data matrix..." : `Found ${totalRecords} active system violations`}
                    </div>
                </div>

                {/* METRIC CARDS GRID */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div className="bg-[#1a1613] rounded-2xl p-5 border border-[#2e2720] shadow-xl relative overflow-hidden group hover:border-[#3e342b] transition-all">
                        <div className="flex justify-between items-start">
                            <div className="space-y-2">
                                <span className="text-xs font-bold uppercase tracking-wider text-stone-400">Outstanding Fines</span>
                                <h3 className="text-xl font-bold text-rose-400 font-mono tracking-tight">
                                    {summary.outstanding_fine.toLocaleString()}៛
                                </h3>
                            </div>
                            <div className="p-2.5 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-400">
                                <ShieldAlert className="h-5 w-5" />
                            </div>
                        </div>
                        <div className="mt-4 flex items-center text-[10px] text-rose-400 font-bold bg-rose-500/10 border border-rose-500/20 px-2.5 py-1 rounded-lg uppercase tracking-wide w-fit">
                            Requires Collection
                        </div>
                    </div>

                    <div className="bg-[#1a1613] rounded-2xl p-5 border border-[#2e2720] shadow-xl relative overflow-hidden group hover:border-[#3e342b] transition-all">
                        <div className="flex justify-between items-start">
                            <div className="space-y-2">
                                <span className="text-xs font-bold uppercase tracking-wider text-stone-400">Total Fines Collected</span>
                                <h3 className="text-xl font-bold text-emerald-400 font-mono tracking-tight">
                                    {summary.total_fine_paid.toLocaleString()}៛
                                </h3>
                            </div>
                            <div className="p-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-emerald-400">
                                <TrendingUp className="h-5 w-5" />
                            </div>
                        </div>
                        <div className="mt-4 flex items-center text-[10px] text-emerald-400 font-bold bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-lg uppercase tracking-wide w-fit">
                            Settled Transactions
                        </div>
                    </div>

                    <div className="bg-[#1a1613] rounded-2xl p-5 border border-[#2e2720] shadow-xl relative overflow-hidden group hover:border-[#3e342b] transition-all">
                        <div className="flex justify-between items-start">
                            <div className="space-y-2">
                                <span className="text-xs font-bold uppercase tracking-wider text-stone-400">Gross Aggregate Fines</span>
                                <h3 className="text-xl font-bold text-stone-100 font-mono tracking-tight">
                                    {summary.total_fine_all.toLocaleString()}៛
                                </h3>
                            </div>
                            <div className="p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-400">
                                <DollarSign className="h-5 w-5" />
                            </div>
                        </div>
                        <div className="mt-4 flex items-center text-[10px] text-stone-400 font-bold bg-[#131110] border border-[#2e2720] px-2.5 py-1 rounded-lg uppercase tracking-wide w-fit">
                            Lifetime Accumulation
                        </div>
                    </div>
                </div>

                {/* DATA PLATFORMS */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                    
                    {/* DEBTOR STANDINGS CARD */}
                    <div className="bg-[#1a1613] border border-[#2e2720] shadow-xl rounded-2xl overflow-hidden lg:col-span-1 flex flex-col justify-between min-h-[420px]">
                        <div>
                            <div className="p-4 border-b border-[#2e2720] flex items-center gap-2 bg-[#131110]">
                                <Users className="h-4 w-4 text-stone-400" />
                                <h3 className="text-xs font-bold text-stone-200 uppercase tracking-wider">Debtor Standings</h3>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse table-fixed text-xs">
                                    <thead>
                                        <tr className="bg-[#131110]/50 text-stone-400 text-[10px] font-bold uppercase tracking-wider border-b border-[#2e2720]">
                                            <th className="p-3.5 w-3/5">Student Patron</th>
                                            <th className="p-3.5 w-2/5 text-right">Balance Due</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[#2e2720] text-stone-300">
                                        {currentStudents.map((s) => (
                                            <tr key={s.patron_id} className="hover:bg-[#131110]/50 transition-colors group">
                                                <td className="p-3.5 text-stone-100 font-medium truncate">
                                                    {s.patron_name}
                                                </td>
                                                <td className="p-3.5 text-right font-bold font-mono text-rose-400">
                                                    {s.total_fine.toLocaleString()}៛
                                                </td>
                                            </tr>
                                        ))}

                                        {students.length === 0 && (
                                            <tr>
                                                <td className="p-12 text-center" colSpan={2}>
                                                    <div className="flex flex-col items-center justify-center text-stone-500 gap-1.5">
                                                        <Inbox className="h-6 w-6 stroke-[1.5]" />
                                                        <p className="text-xs font-bold text-stone-400">No debtor entries found.</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Debtor Pagination Shelf */}
                        <div className="bg-[#131110] px-4 py-3 border-t border-[#2e2720] flex items-center justify-between">
                            <span className="text-[11px] text-stone-400 font-mono">
                                {debtorPage}/{totalDebtorPages}
                            </span>
                            <div className="flex items-center space-x-1">
                                <button
                                    onClick={() => setDebtorPage((p) => Math.max(1, p - 1))}
                                    disabled={debtorPage === 1}
                                    className="p-1 bg-[#1a1613] border border-[#2e2720] rounded text-stone-300 disabled:opacity-30 hover:bg-[#201c17] transition-colors focus:outline-none"
                                >
                                    <ChevronLeft className="w-3.5 h-3.5" />
                                </button>
                                <button
                                    onClick={() => setDebtorPage((p) => Math.min(totalDebtorPages, p + 1))}
                                    disabled={debtorPage === totalDebtorPages}
                                    className="p-1 bg-[#1a1613] border border-[#2e2720] rounded text-stone-300 disabled:opacity-30 hover:bg-[#201c17] transition-colors focus:outline-none"
                                >
                                    <ChevronRight className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* DETAILED MANIFEST LEDGER */}
                    <div className="bg-[#1a1613] border border-[#2e2720] shadow-xl rounded-2xl overflow-hidden lg:col-span-2 flex flex-col justify-between min-h-[420px] relative">
                        {loading && (
                            <div className="absolute inset-0 bg-[#1a1613]/80 backdrop-blur-xs z-10 flex items-center justify-center text-xs font-bold text-amber-400 animate-pulse">
                                Updating records...
                            </div>
                        )}

                        <div>
                            <div className="p-4 border-b border-[#2e2720] flex items-center gap-2 bg-[#131110]">
                                <BookOpen className="h-4 w-4 text-stone-400" />
                                <h3 className="text-xs font-bold text-stone-200 uppercase tracking-wider">Fine Specifications Manifest</h3>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse min-w-[600px] table-fixed text-xs">
                                    <thead>
                                        <tr className="bg-[#131110]/50 text-stone-400 text-[10px] font-bold uppercase tracking-wider border-b border-[#2e2720]">
                                            <th className="p-3.5 w-1/4">Student</th>
                                            <th className="p-3.5 w-1/4">Book Title Target</th>
                                            <th className="p-3.5 w-1/6">Date Due</th>
                                            <th className="p-3.5 w-1/6">Status State</th>
                                            <th className="p-3.5 w-1/6 text-right">Fine</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[#2e2720] text-stone-300">
                                        {loans.map((loan) => (
                                            <tr key={loan.fine_id} className="hover:bg-[#131110]/50 transition-colors group">
                                                <td className="p-3.5 font-semibold text-stone-100 truncate">{loan.patron_name}</td>
                                                <td className="p-3.5 text-stone-300 truncate">{loan.book_title}</td>
                                                <td className="p-3.5 text-stone-400 font-mono text-[11px]">{loan.due_date}</td>
                                                <td className="p-3.5">
                                                    {loan.is_paid ? (
                                                        <span className="inline-flex items-center text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded-md uppercase">
                                                            Settled
                                                        </span>
                                                    ) : loan.returned_date ? (
                                                        <span className="inline-flex items-center text-[10px] font-bold bg-stone-800 text-stone-300 border border-stone-700 px-2 py-0.5 rounded-md">
                                                            Ret: {loan.returned_date}
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wide bg-rose-500/10 text-rose-400 border border-rose-500/20 px-2 py-0.5 rounded-md">
                                                            Overdue
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="p-3.5 text-right font-mono font-bold tracking-tight">
                                                    <div className="flex flex-col items-end gap-1">
                                                        <span className={loan.is_paid ? "text-stone-500 line-through" : "text-rose-400"}>
                                                            {loan.fine.toLocaleString()}៛
                                                        </span>
                                                        
                                                        {!loan.is_paid && (
                                                            <button
                                                                onClick={() => handleOpenPaymentModal(loan)}
                                                                className="text-[10px] font-bold uppercase bg-amber-500 hover:bg-amber-400 text-stone-950 px-2 py-0.5 rounded-md transition-all opacity-0 group-hover:opacity-100 focus:opacity-100 cursor-pointer shadow-sm"
                                                            >
                                                                Collect Pay
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}

                                        {loans.length === 0 && !loading && (
                                            <tr>
                                                <td className="p-16 text-center" colSpan={5}>
                                                    <div className="flex flex-col items-center justify-center text-stone-500 gap-2">
                                                        <Inbox className="h-8 w-8 stroke-[1.2]" />
                                                        <p className="text-xs font-bold text-stone-400">No matching assets found.</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Manifest Pagination Shelf */}
                        <div className="bg-[#131110] px-6 py-3.5 border-t border-[#2e2720] flex items-center justify-between">
                            <span className="text-xs text-stone-400">
                                Showing page <span className="text-stone-100 font-bold font-mono">{manifestPage}</span> of <span className="text-stone-100 font-bold font-mono">{totalManifestPages}</span>
                            </span>
                            <div className="flex items-center space-x-1.5">
                                <button
                                    onClick={() => setManifestPage((p) => Math.max(1, p - 1))}
                                    disabled={manifestPage === 1 || loading}
                                    className="p-1.5 bg-[#1a1613] border border-[#2e2720] rounded-lg text-stone-300 disabled:opacity-30 hover:bg-[#201c17] transition-colors focus:outline-none"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                <div className="text-xs font-bold text-stone-200 px-3 bg-[#1a1613] border border-[#2e2720] h-8 flex items-center rounded-lg font-mono">
                                    Page {manifestPage}
                                </div>
                                <button
                                    onClick={() => setManifestPage((p) => Math.min(totalManifestPages, p + 1))}
                                    disabled={manifestPage === totalManifestPages || loading}
                                    className="p-1.5 bg-[#1a1613] border border-[#2e2720] rounded-lg text-stone-300 disabled:opacity-30 hover:bg-[#201c17] transition-colors focus:outline-none"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- CONFIRM PAYMENT MODAL PORTAL --- */}
                {isModalOpen && selectedLoan && (
                    <div className="fixed inset-0 font-dynamic-title bg-stone-950/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                        <div className="bg-[#1a1613] w-full max-w-md rounded-2xl border border-[#2e2720] shadow-2xl overflow-hidden transform transition-all">
                            {/* Header Banner */}
                            <div className="bg-[#131110] border-b border-[#2e2720] px-6 py-4 flex items-center justify-between text-stone-100">
                                <div className="flex items-center gap-2.5">
                                    <div className="p-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-emerald-400">
                                        <DollarSign className="w-4 h-4" />
                                    </div>
                                    <h3 className="text-sm font-bold uppercase tracking-wider">Confirm Fee Reception</h3>
                                </div>
                                <button 
                                    onClick={() => setIsModalOpen(false)}
                                    className="text-stone-400 hover:text-stone-200 hover:bg-[#201c17] p-1 rounded-lg transition-colors cursor-pointer"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Details Grid */}
                            <div className="p-6 space-y-4">
                                <p className="text-xs text-stone-400 leading-relaxed">
                                    Verify that the student has processed their fine payment manually at the counter before completing this transaction.
                                </p>

                                <div className="bg-[#131110] rounded-xl p-4 border border-[#2e2720] space-y-3 text-xs">
                                    <div className="flex justify-between items-center border-b border-[#2e2720] pb-2">
                                        <span className="font-bold text-stone-400 uppercase text-[10px]">Student Patron</span>
                                        <span className="font-semibold text-stone-100">{selectedLoan.patron_name}</span>
                                    </div>
                                    <div className="flex justify-between items-start border-b border-[#2e2720] pb-2">
                                        <span className="font-bold text-stone-400 uppercase text-[10px] mt-0.5">Asset Checked</span>
                                        <span className="font-medium text-stone-300 text-right max-w-[200px] truncate">{selectedLoan.book_title}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="font-bold text-stone-400 uppercase text-[10px]">Due Date Target</span>
                                        <span className="font-mono font-bold text-stone-300 bg-[#1a1613] px-2 py-0.5 rounded border border-[#2e2720] text-[11px]">{selectedLoan.due_date}</span>
                                    </div>
                                </div>

                                {/* Large Callout Box for Total Due */}
                                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 flex items-center justify-between">
                                    <div className="flex items-center gap-2.5">
                                        <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                                        <div>
                                            <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-wide">Total Amount Settling</p>
                                            <p className="text-[11px] text-emerald-300/80">Clears current record balance</p>
                                        </div>
                                    </div>
                                    <div className="text-xl font-bold font-mono text-emerald-400 tracking-tight">
                                        {selectedLoan.fine.toLocaleString()}៛
                                    </div>
                                </div>
                            </div>

                            {/* Modal Action Footer */}
                            <div className="bg-[#131110] px-6 py-4 border-t border-[#2e2720] flex items-center justify-end gap-2.5">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    disabled={submittingPayment}
                                    className="px-4 h-9 text-xs font-bold text-stone-400 hover:text-stone-200 bg-[#1a1613] border border-[#2e2720] rounded-xl hover:bg-[#201c17] transition-all cursor-pointer disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirmPayment}
                                    disabled={submittingPayment}
                                    className="px-5 h-9 text-xs font-bold uppercase tracking-wider text-stone-950 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer shadow-sm"
                                >
                                    {submittingPayment ? "Processing..." : "Confirm & Settle"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AppLayout>
    );
}